import { inject, Injectable, signal } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

import {
    BayDrawer,
    CableAction,
    CableActionEnum,
    DrawerReferenceData,
    FiberingSideEnum,
    LinkToDelete,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { PositionEditModalResult } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/position-edit-modal/position-edit-modal.component';
import {
    CableDirectionEnum,
    CassetteReferencePosition,
    SynopticBoxPlanCable,
    SynopticBoxPlanCableBySide,
    SynopticBoxPlanFiber,
} from '@connect-services/client/sdk/api';
import { BoxPlanEditModeStateService } from '@connect-services/client/services/synoptic/edit/box-plan-edit-mode-state.service';
import { SynopticBayPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-bay-plan-edit.service';
import { SynopticPlanEditModalService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-modal.service';

const entranceFiberKeys: Array<keyof SynopticPlanItem> = [
    'boite_baie_codeext_entrante',
    'boite_baie_entrante',
    'boite_baie_etiquet_entrante',
    'cable_codeext_entrant',
    'cable_entrant',
    'cable_etiquet_entrant',
    'fibre_entrante',
    'numero_fibre_entrante',
    'numero_tube_entrant',
    'type_fibre_entrante',
];
const exitFiberKeys: Array<keyof SynopticPlanItem> = [
    'boite_baie_codeext_sortante',
    'boite_baie_etiquet_sortante',
    'boite_baie_sortante',
    'cable_codeext_sortant',
    'cable_etiquet_sortant',
    'cable_sortant',
    'fibre_sortante',
    'numero_fibre_sortante',
    'numero_tube_sortant',
    'type_fibre_sortante',
];

export type CableTubeAnchor = { cableCode: string; side: CableDirectionEnum; tubeIndex: number };
export type CassetteAnchor = { key: string; side: CableDirectionEnum };

export type CableFiberAnchor = {
    cableCode: string;
    fiberCode: string;
    side: CableDirectionEnum;
    tubeIndex: number;
};

export type CassettePositionAnchor = {
    key: string;
    position: string;
    side: CableDirectionEnum;
};

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanEditService {
    public readonly selectedCableFiber = signal<CableFiberAnchor | null>(null);
    public readonly selectedCables = signal<Set<string>>(new Set());
    public readonly selectedCassetteAnchor = signal<CassetteAnchor | null>(null);
    public readonly selectedCassettePosition = signal<CassettePositionAnchor | null>(null);
    public readonly selectedCassettes = signal<Set<string>>(new Set());
    public readonly selectedTubeAnchor = signal<CableTubeAnchor | null>(null);

    private readonly boxPlanEditModeStateService = inject(BoxPlanEditModeStateService);
    private readonly synopticBayPlanEditService = inject(SynopticBayPlanEditService);
    private readonly synopticPlanEditModalService = inject(SynopticPlanEditModalService);

    public addCassette(
        cassettePlan: SynopticPlanItem[][],
        cassette: CassetteReferencePosition,
        drawerData?: (DrawerReferenceData & { drawerCode?: string | null; drawerId: string | null }) | BayDrawer,
    ): SynopticPlanItem[][] {
        const positionCount = cassette.nb_position ?? 0;
        if (!positionCount) {
            return cassettePlan;
        }

        const cassetteNumber = (
            Math.max(
                0,
                ...cassettePlan.flatMap((cassette: SynopticPlanItem[]) => {
                    return cassette.map((cassetteItem: SynopticPlanItem) => Number(cassetteItem.numero_cassette) || 0);
                }),
            ) + 1
        ).toString();

        const cassetteId = uuidv4();
        const drawerDataSpread = drawerData
            ? 'drawerId' in drawerData
                ? {
                      cote_tiroir: drawerData.side,
                      position_tiroir: drawerData.position,
                      reference_tiroir: drawerData.referenceCode,
                      taille_tiroir: drawerData.drawerLength,
                      ti_uuid: drawerData.drawerId,
                      tiroir: drawerData.drawerCode ?? null,
                  }
                : {
                      cote_tiroir: drawerData.side,
                      position_tiroir: drawerData.position,
                      reference_tiroir: drawerData.reference,
                      taille_tiroir: drawerData.length.toString(),
                      ti_uuid: drawerData.id,
                      tiroir: drawerData.code,
                  }
            : {};
        const newCassette: SynopticPlanItem[] = Array.from({ length: positionCount }, (_: unknown, index: number) => {
            const keys = this.getKeysToOmitOnReset(FiberingSideEnum.ALL);
            keys.push('affectation', 'connectorisation', 'type_epissurage', 'type_ingenierie');
            const partialItem: Partial<SynopticPlanItem> = {
                cassette: `cassette-${cassetteNumber}`,
                cs_uuid: cassetteId,
                numero_cassette: cassetteNumber,
                numero_position: (index + 1).toString(),
                ps_uuid: uuidv4(),
                ...drawerDataSpread,
            };

            const nullProperties: Partial<SynopticPlanItem> = keys.reduce(
                (acc: Partial<SynopticPlanItem>, key: keyof SynopticPlanItem) => {
                    return { ...acc, [key]: null };
                },
                {},
            );

            return { ...partialItem, ...nullProperties } as SynopticPlanItem;
        });

        return [...cassettePlan, newCassette];
    }

    public addLinkFiberCassettePosition(
        cassettes: SynopticPlanItem[][],
        cable: SynopticBoxPlanCable,
        cassetteAnchor: CassettePositionAnchor,
        cableFiberAnchor: CableFiberAnchor,
        drawer?: BayDrawer,
    ): SynopticPlanItem[][] {
        const isLeft = cassetteAnchor.side === CableDirectionEnum.LEFT;
        const fiber = cable.tubes[cableFiberAnchor.tubeIndex]?.find(
            (tubeFiber: SynopticBoxPlanFiber) => tubeFiber.code === cableFiberAnchor.fiberCode,
        );

        if (!fiber || this.isFiberAlreadyAssigned(cassettes, fiber.code, isLeft)) {
            return cassettes;
        }

        return cassettes.map((cassette: SynopticPlanItem[]) => {
            return cassette.map((cassetteItem: SynopticPlanItem) => {
                const isTargetPosition =
                    cassetteItem.cassette === cassetteAnchor.key && cassetteItem.ps_uuid === cassetteAnchor.position;

                const alreadyFilled = isLeft ? cassetteItem.fibre_entrante : cassetteItem.fibre_sortante;

                if (!isTargetPosition || alreadyFilled) {
                    return cassetteItem;
                }

                return this.addFibersToCassetteSide(cassetteItem, cable, cableFiberAnchor, fiber, {
                    drawer: drawer,
                    isLeft: isLeft,
                });
            });
        });
    }

    public addLinkTubeCassette(
        cassettes: SynopticPlanItem[][],
        cable: SynopticBoxPlanCable,
        cassetteAnchor: CassetteAnchor,
        cableTubeAnchor: CableTubeAnchor,
        drawer?: BayDrawer,
    ): SynopticPlanItem[][] {
        let availableFiberIndex = 0;
        const isLeft = cassetteAnchor.side === CableDirectionEnum.LEFT;
        const availableFibers = this.getAvailableFibers(
            drawer ? this.synopticBayPlanEditService.cassettes() : cassettes,
            cable.tubes[cableTubeAnchor.tubeIndex],
            isLeft,
        );

        return cassettes.map((cassette: SynopticPlanItem[]) => {
            return cassette.map((cassetteItem: SynopticPlanItem) => {
                const isTargetCassette = cassetteItem.cassette === cassetteAnchor.key;
                const hasAvailableFiber = availableFiberIndex < availableFibers.length;
                const alreadyFilled = isLeft ? cassetteItem.fibre_entrante : cassetteItem.fibre_sortante;

                if (!isTargetCassette || !hasAvailableFiber || alreadyFilled) {
                    return cassetteItem;
                }

                const updatedItem = this.addFibersToCassetteSide(
                    cassetteItem,
                    cable,
                    cableTubeAnchor,
                    availableFibers[availableFiberIndex],
                    { drawer: drawer, isLeft: isLeft },
                );

                availableFiberIndex++;

                return updatedItem;
            });
        });
    }

    public getCablesToSelect(
        sideCables: SynopticBoxPlanCableBySide,
        fiberingSide: FiberingSideEnum,
    ): SynopticBoxPlanCableBySide {
        if (!this.selectedCables().size && this.selectedCassettes().size) {
            fiberingSide = FiberingSideEnum.ALL;
        }

        switch (fiberingSide) {
            case FiberingSideEnum.ENTERING:
                return { enteringCables: sideCables.enteringCables, outgoingCables: [] };
            case FiberingSideEnum.OUTGOING:
                return { enteringCables: [], outgoingCables: sideCables.outgoingCables };
            case FiberingSideEnum.ALL:
                return sideCables;
            case FiberingSideEnum.SELECTION:
                return {
                    enteringCables: sideCables.enteringCables.filter((cable: SynopticBoxPlanCable) =>
                        this.selectedCables().has(cable.code),
                    ),
                    outgoingCables: sideCables.outgoingCables.filter((cable: SynopticBoxPlanCable) =>
                        this.selectedCables().has(cable.code),
                    ),
                };
            default:
                throw new Error('The value received is not a part of FiberingSideEnum');
        }
    }

    public getCassettesToSelect(
        currentCassettes: Array<[string, SynopticPlanItem[]]>,
    ): Array<[string, SynopticPlanItem[]]> {
        if (!this.selectedCassettes().size) {
            return currentCassettes;
        }

        return currentCassettes.filter(([key]: [string, SynopticPlanItem[]]) => this.selectedCassettes().has(key));
    }

    public getKeysToOmitOnReset(type: FiberingSideEnum): Array<keyof SynopticPlanItem> {
        switch (type) {
            case FiberingSideEnum.ENTERING:
                return entranceFiberKeys;
            case FiberingSideEnum.OUTGOING:
                return exitFiberKeys;
            default:
                return [...entranceFiberKeys, ...exitFiberKeys];
        }
    }

    public getResetCassetteItem(
        cassetteItem: SynopticPlanItem,
        fiberingSide: FiberingSideEnum,
        cassettesToReset: Array<[string, SynopticPlanItem[]]>,
        cablesToReset: SynopticBoxPlanCableBySide,
    ): SynopticPlanItem {
        if (fiberingSide !== FiberingSideEnum.SELECTION) {
            return this.resetCassetteItemKeys(
                cassetteItem,
                this.getKeysToOmitOnReset(fiberingSide),
                cablesToReset.enteringCables,
            );
        }

        const hasCassetteToReset = cassettesToReset.some(
            ([cassetteCode]: [string, SynopticPlanItem[]]) => cassetteCode === cassetteItem.cassette,
        );
        if (!hasCassetteToReset) {
            return cassetteItem;
        }

        const hasEnterCableSelected = cablesToReset.enteringCables.some(
            (cable: SynopticBoxPlanCable) => cable.code === cassetteItem.cable_entrant,
        );
        const hasExitCableSelected = cablesToReset.outgoingCables.some(
            (cable: SynopticBoxPlanCable) => cable.code === cassetteItem.cable_sortant,
        );

        if (hasEnterCableSelected && hasExitCableSelected) {
            return this.resetCassetteItemKeys(
                cassetteItem,
                this.getKeysToOmitOnReset(FiberingSideEnum.ALL),
                cablesToReset.enteringCables,
            );
        }

        if (hasEnterCableSelected) {
            return this.resetCassetteItemKeys(
                cassetteItem,
                this.getKeysToOmitOnReset(FiberingSideEnum.ENTERING),
                cablesToReset.enteringCables,
            );
        }

        if (hasExitCableSelected) {
            return this.resetCassetteItemKeys(
                cassetteItem,
                this.getKeysToOmitOnReset(FiberingSideEnum.OUTGOING),
                cablesToReset.enteringCables,
            );
        }

        return cassetteItem;
    }

    public isMatchingLink(cassetteItem: SynopticPlanItem, linkToDelete: LinkToDelete): boolean {
        if (cassetteItem.cassette !== linkToDelete.cassetteKey) {
            return false;
        }

        const isLeft = linkToDelete.side === CableDirectionEnum.LEFT;
        const cable = isLeft ? cassetteItem.cable_entrant : cassetteItem.cable_sortant;

        if (cable !== linkToDelete.cable) {
            return false;
        }

        const fiber = isLeft ? cassetteItem.fibre_entrante : cassetteItem.fibre_sortante;
        const tube = isLeft ? cassetteItem.numero_tube_entrant : cassetteItem.numero_tube_sortant;

        if (linkToDelete.type === 'fiber') {
            return fiber === linkToDelete.fiber;
        }

        return Number(tube) === linkToDelete.tube;
    }

    public async leaveEdition(projectId: string, layerId: string, entityId: string): Promise<boolean> {
        if (!projectId) {
            return true;
        }

        const editProjectState = this.boxPlanEditModeStateService.getState(projectId);
        const currentState = editProjectState[layerId]?.[entityId];

        if (!currentState) {
            return true;
        }

        const hasConfirmEdition = await this.synopticPlanEditModalService.openLeaveEditionConfirmModal();
        if (!hasConfirmEdition) {
            return false;
        }

        this.boxPlanEditModeStateService.clearEditModeState(projectId);

        return true;
    }

    public moveCableUpAndDown(
        cables: SynopticBoxPlanCableBySide,
        cableAction: CableAction,
        side: CableDirectionEnum,
    ): SynopticBoxPlanCableBySide {
        const isLeft = side === CableDirectionEnum.LEFT;
        const key: keyof SynopticBoxPlanCableBySide = isLeft ? 'enteringCables' : 'outgoingCables';

        const updatedCables = [...cables[key]];
        const cableIndex = updatedCables.findIndex((cable: SynopticBoxPlanCable) => cable.code === cableAction.code);
        const element = updatedCables[cableIndex];

        updatedCables.splice(cableIndex, 1);
        updatedCables.splice(cableAction.action === CableActionEnum.UP ? cableIndex - 1 : cableIndex + 1, 0, element);

        return { ...cables, [key]: updatedCables };
    }

    public removeCableFromSide(
        sideCables: SynopticBoxPlanCableBySide,
        cableCodeToRemove: string,
        side: CableDirectionEnum,
    ): SynopticBoxPlanCableBySide {
        if (side === CableDirectionEnum.LEFT) {
            return {
                ...sideCables,
                enteringCables: this.removeCableFromList(sideCables.enteringCables, cableCodeToRemove),
            };
        }

        return {
            ...sideCables,
            outgoingCables: this.removeCableFromList(sideCables.outgoingCables, cableCodeToRemove),
        };
    }

    public removeCableLinksFromCassettes(
        cassettes: SynopticPlanItem[][],
        cassettesByKeys: Array<[string, SynopticPlanItem[]]>,
        cableCodeToRemove: string,
        cables: SynopticBoxPlanCableBySide,
        side: CableDirectionEnum,
    ): SynopticPlanItem[][] {
        return cassettes.map((cassette: SynopticPlanItem[]) => {
            return cassette.map((cassetteItem: SynopticPlanItem) => {
                if (!this.isCassetteItemLinkedToCable(cassetteItem, cableCodeToRemove, side)) {
                    return cassetteItem;
                }

                const fiberingSide =
                    side === CableDirectionEnum.LEFT ? FiberingSideEnum.ENTERING : FiberingSideEnum.OUTGOING;

                return this.getResetCassetteItem(cassetteItem, fiberingSide, cassettesByKeys, cables);
            });
        });
    }

    public resetSelections(): void {
        this.selectedCables.set(new Set());
        this.selectedCassettes.set(new Set());
        this.selectedCassetteAnchor.set(null);
        this.selectedTubeAnchor.set(null);
        this.selectedCassettePosition.set(null);
        this.selectedCableFiber.set(null);
    }

    public updateCassettePosition(
        cassette: SynopticPlanItem[],
        positionValue: PositionEditModalResult,
        fiber?: SynopticPlanItem,
    ): SynopticPlanItem[] {
        return cassette.map((cassetteItem: SynopticPlanItem) => {
            if (fiber && cassetteItem !== fiber) {
                return cassetteItem;
            }

            return {
                ...cassetteItem,
                affectation: positionValue.assignment,
                connectorisation: positionValue.function,
                type_ingenierie: positionValue.engineeringType,
            };
        });
    }

    private addFibersToCassetteSide(
        cassetteItem: SynopticPlanItem,
        cable: SynopticBoxPlanCable,
        cableTubeAnchor: CableTubeAnchor,
        fiber: SynopticBoxPlanFiber,
        options: { drawer?: BayDrawer; isLeft: boolean },
    ): SynopticPlanItem {
        const side = options.isLeft ? 'entrant' : 'sortant';

        const updatedItem = {
            ...cassetteItem,
            [`boite_baie_codeext_${side}e`]: cable.boxExternalCode || cable.bayExternalCode,
            [`boite_baie_${side}e`]: cable.boxCode || cable.bayCode,
            [`boite_baie_etiquet_${side}e`]: cable.boxLabel || cable.bayLabel,
            [`cable_codeext_${side}`]: cable.externalCode,
            [`cable_${side}`]: cable.code,
            [`cable_etiquet_${side}`]: cable.label,
            [`fibre_${side}e`]: fiber.code,
            [`numero_fibre_${side}e`]: fiber.position.toString(),
            [`numero_tube_${side}`]: cableTubeAnchor.tubeIndex.toString(),
            [`type_fibre_${side}e`]: 'G567A2',
            ...(options.drawer && {
                position_tiroir: options.drawer.position,
                reference_tiroir: options.drawer.reference,
                taille_tiroir: options.drawer.length.toString(),
                ti_uuid: options.drawer.id,
                tiroir: options.drawer.code,
            }),
        };

        if (options.isLeft) {
            return { ...updatedItem, est_en_continuite: this.isContinuousLink(updatedItem, cable) };
        }

        return updatedItem;
    }

    private getAvailableFibers(
        cassettes: SynopticPlanItem[][],
        fibers: SynopticBoxPlanFiber[],
        isLeft: boolean,
    ): SynopticBoxPlanFiber[] {
        return fibers.filter((fiber: SynopticBoxPlanFiber) => {
            return cassettes.every((cassette: SynopticPlanItem[]) => {
                return cassette.every((cassetteItem: SynopticPlanItem) => {
                    if (isLeft) {
                        return cassetteItem.fibre_entrante !== fiber.code;
                    }

                    return cassetteItem.fibre_sortante !== fiber.code;
                });
            });
        });
    }

    private isCassetteItemLinkedToCable(
        cassetteItem: SynopticPlanItem,
        cableCode: string,
        side: CableDirectionEnum,
    ): boolean {
        const linkedCable = side === CableDirectionEnum.LEFT ? cassetteItem.cable_entrant : cassetteItem.cable_sortant;

        return linkedCable === cableCode;
    }

    private isContinuousLink(cassetteItem: SynopticPlanItem, cable: SynopticBoxPlanCable): boolean {
        if (!cassetteItem.numero_tube_entrant) {
            return false;
        }
        const enteringFiber = Object.values(cable.tubes[cassetteItem.numero_tube_entrant]).find(
            (fiber: SynopticBoxPlanFiber) => fiber.code === cassetteItem.fibre_entrante,
        );

        return !!enteringFiber?.isContinue;
    }

    private isFiberAlreadyAssigned(cassettes: SynopticPlanItem[][], fiberCode: string, isLeft: boolean): boolean {
        return cassettes
            .flat()
            .some((cassetteItem: SynopticPlanItem) =>
                isLeft ? cassetteItem.fibre_entrante === fiberCode : cassetteItem.fibre_sortante === fiberCode,
            );
    }

    private removeCableFromList(cables: SynopticBoxPlanCable[], cableCodeToRemove: string): SynopticBoxPlanCable[] {
        return cables.filter((cable: SynopticBoxPlanCable) => cable.code !== cableCodeToRemove);
    }

    private resetCassetteItemKeys(
        cassetteItem: SynopticPlanItem,
        keysToReset: Array<keyof SynopticPlanItem>,
        incomingCables: SynopticBoxPlanCable[],
    ): SynopticPlanItem {
        const updatedItem: SynopticPlanItem = { ...cassetteItem };
        keysToReset.forEach((key: keyof SynopticPlanItem) => {
            (updatedItem[key] as string | null) = null;
        });

        const cable = incomingCables.find((cable: SynopticBoxPlanCable) => cable.code === cassetteItem.cable_entrant);
        if (cable) {
            updatedItem.est_en_continuite = this.isContinuousLink(updatedItem, cable);
        }

        return updatedItem;
    }
}
